import deLocale from '@uploadcare/file-uploader/locales/file-uploader/de.js';
import enLocale from '@uploadcare/file-uploader/locales/file-uploader/en.js';
import type { OutputCollectionState, OutputFileEntry, UploadCtxProvider } from '@uploadcare/react-uploader';
import { defineLocale, FileUploaderRegular } from '@uploadcare/react-uploader';
import '@uploadcare/react-uploader/core.css';
import { PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { UPLOADCARE_CDN_NAME, UPLOADCARE_PUBLIC_KEY } from '../../../lib/constants.ts';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { UploadedFile } from '../../../server/dto/crm-attachment-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { FileCard } from '../../crm-attachment/components/file-card.tsx';

defineLocale('de', deLocale);
defineLocale('en', enLocale);

type TaskFileUploaderProps = {
    value: UploadedFile[];
    onChange: (files: UploadedFile[]) => void;
};

export const TaskFileUploader = ({ value, onChange }: TaskFileUploaderProps) => {
    const { t, locale } = useLocale('tasks');
    const { tenantId, platoUser } = useAppState();
    const { data: uploadSignature, isLoading } = api.platoUser.query.uploadcare.getUploadSignature.useQuery();
    const uploaderRef = useRef<UploadCtxProvider | null>(null);
    const sendEvent = useAnalytics();

    const uploadMetadata = useMemo(
        () => ({
            tenantId: tenantId ?? '',
            userId: platoUser?.id ?? '',
        }),
        [tenantId, platoUser?.id],
    );

    const handleDoneClick = useCallback(
        (event: OutputCollectionState) => {
            const newFiles: UploadedFile[] = event.successEntries.map(
                (entry: OutputFileEntry<'success'>) => ({
                    externalUuid: entry.uuid,
                    cdnUrl: entry.cdnUrl,
                    cdnUrlModifiers: entry.cdnUrlModifiers,
                    name: entry.name,
                    size: entry.size,
                    mimeType: entry.mimeType,
                    isImage: entry.isImage,
                    s3Url: entry.fileInfo.s3Url ?? null,
                    s3Bucket: entry.fileInfo.s3Bucket ?? null,
                }),
            );

            const existingUuids = new Set(value.map((f) => f.externalUuid));
            for (const entry of event.successEntries) {
                if (!existingUuids.has(entry.uuid)) {
                    sendEvent('attachment_uploaded', { context: 'task', file_type: entry.mimeType });
                }
            }

            onChange(newFiles);
        },
        [onChange, sendEvent, value],
    );

    const handleFileUploadFailed = useCallback(
        (entry: OutputFileEntry<'failed'>) => {
            sendEvent('attachment_upload_failed', { context: 'task', file_type: entry.mimeType });
        },
        [sendEvent],
    );

    const handleRemove = useCallback(
        (fileToRemove: UploadedFile) => {
            const uploaderApi = uploaderRef.current?.getAPI();
            const collectionState = uploaderApi?.getOutputCollectionState();
            const entry = collectionState?.allEntries.find(
                (entry) => entry.uuid === fileToRemove.externalUuid,
            );

            if (entry) {
                uploaderApi?.removeFileByInternalId(entry.internalId);
            }

            sendEvent('attachment_deleted', { context: 'task', file_type: fileToRemove.mimeType });

            onChange(value.filter((file) => file.externalUuid !== fileToRemove.externalUuid));
        },
        [value, onChange, sendEvent],
    );

    const handleDownload = useCallback(
        (file: UploadedFile) => {
            sendEvent('attachment_downloaded', { context: 'task', file_type: file.mimeType });
        },
        [sendEvent],
    );

    const apiRefCallback = useCallback(
        (instance: UploadCtxProvider | null) => {
            uploaderRef.current = instance;

            if (!instance) {
                return;
            }

            const uploaderApi = instance.getAPI();
            const collecitonState = uploaderApi.getOutputCollectionState();

            for (const file of value) {
                const isAlreadyAdded = collecitonState.allEntries.some(
                    (entry) => entry.uuid === file.externalUuid,
                );

                if (!isAlreadyAdded) {
                    uploaderApi.addFileFromCdnUrl(file.cdnUrl, {
                        silent: true,
                        source: 'js-api',
                    });
                }
            }
        },
        [value],
    );

    const handleAddFiles = useCallback(() => {
        const uploaderApi = uploaderRef.current?.getAPI();

        if (uploaderApi) {
            uploaderApi.initFlow();
        }
    }, []);

    if (isLoading || !uploadSignature) {
        return null;
    }

    return (
        <div className="flex min-w-0 grow flex-col gap-3 items-start self-stretch">
            {value.length > 0 && (
                <div className="flex min-w-0 w-full flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                    {value.map((file) => (
                        <div key={file.externalUuid} className="w-40 shrink-0">
                            <FileCard file={file} onRemove={handleRemove} onDownload={handleDownload} />
                        </div>
                    ))}
                </div>
            )}
            <Button variant="link" icon={PlusIcon} onClick={handleAddFiles} className="p-2">
                {t('tasks__form__add_files')}
            </Button>
            <FileUploaderRegular
                apiRef={apiRefCallback}
                pubkey={String(UPLOADCARE_PUBLIC_KEY)}
                cdnCname={String(UPLOADCARE_CDN_NAME)}
                classNameUploader="uc-light"
                sourceList="local, camera"
                userAgentIntegration="llm-react"
                filesViewMode="list"
                localeName={locale}
                secureSignature={uploadSignature.signature}
                secureExpire={String(uploadSignature.expire)}
                metadata={uploadMetadata}
                onDoneClick={handleDoneClick}
                onFileUploadFailed={handleFileUploadFailed}
                headless
            />
        </div>
    );
};
